import { cva, type VariantProps } from "@msuite/ui/libs/cva";
import { Popover } from "@msuite/ui/popover";
import { useQuery } from "@msuite/ui/query";
import { cn, responseHandler } from "@msuite/ui/utils";
import type { Nullable, Tables } from "@msuite/utils/types";
import { OrganizationPopover } from "@/components/popovers/organization-popover";
import { client } from "@/hono";
import { OrganizationAvatar } from "./organization-avatar";

/** Props Interface */
interface OrganizationCardProps extends VariantProps<typeof organizationCard> {
	orgId: Nullable<string>;
	withAvatar?: boolean;
	withPopover?: boolean;
	suffix?: string | number | null;
	render?: (org: Nullable<Tables<"organizations">>) => React.ReactNode;
}

export function OrganizationCard({
	orgId,
	variant,
	fontSize,
	fontWeight,
	withAvatar = true,
	withPopover = true,
	suffix = null,
	render,
}: OrganizationCardProps) {
	/** Hooks */
	const classes = organizationCard({ variant, fontSize, fontWeight });

	/** Query */
	const { data } = useQuery({
		queryKey: [orgId || ""],
		queryFn: async () => {
			if (!orgId) throw new Error("Organization ID is required");
			const response = await client.organizations[":orgId"].$get({
				param: { orgId },
			});
			const data = await responseHandler(response);
			return {
				organization: data.organization,
				partnership: data.partnership,
			};
		},
		enabled: !!orgId,
	});

	if (!orgId) return null;
	if (render) return render(data?.organization);

	/** Render */
	return (
		<Popover
			trigger="click"
			variant="plain"
			isDisabled={!withPopover}
			render={() => <OrganizationPopover orgId={orgId} />}
		>
			<div className={cn("flex-row items-center gap-2.5", classes)}>
				{withAvatar && <OrganizationAvatar orgId={orgId} />}
				<p className="overflow-ellipsis whitespace-nowrap">
					{data?.organization?.name} {suffix}
				</p>
			</div>
		</Popover>
	);
}

const organizationCard = cva(["flex"], {
	variants: {
		variant: {
			default: ["card"],
			plain: [],
		},
		fontSize: {
			xs: ["text-xs"],
			sm: ["text-sm"],
			md: ["text-base"],
			lg: ["text-lg"],
		},
		fontWeight: {
			normal: ["font-normal"],
			medium: ["font-medium"],
			semibold: ["font-semibold"],
			bold: ["font-bold"],
		},
	},
	defaultVariants: {
		variant: "plain",
		fontSize: "sm",
		fontWeight: "normal",
	},
});
